/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React from 'react';
import './PoliciesAndBestPracticesTable.scss';
import { useAppSelector } from '../../../../../reducers';
import { getLatestReportData } from '../../utils/selectors';
import { getDataAsNumber, getDataAsValueData } from '../../utils/helpers';
import { ReferenceLinks } from '../../QuestionReference';

enum PoliciesAndBestPracticeCode {
  Diversity = 'survey/sdg/10.3/discrimination-policy',
  MentalHeal = 'survey/sdg/3.4/pc-investment-inform-public-risks-product',
  BriberyCorruption = 'survey/sdg/16.4/illicit-flows',
  SustainabilityReporting = 'survey/generic/does-company-report-sustainable-development-risks',
  BonusesLinked = 'survey/sdg/4.7/pc-bonuses-based-on-ees-topics',
  humanTrafficking = 'survey/sdg/10.7/human-trafficking',
}

const greenIcon = <i className='fa fa-check text-ThemeSuccessMedium mr-3' />
const redIcon = <i className='fa fa-times text-ThemeDangerMedium mr-3' />

interface PolicyItem {
  utrCode: PoliciesAndBestPracticeCode;
  hasPositiveValue: boolean;
  name: string
}

export const PoliciesAndBestPracticesTable = React.memo(() => {
  const initiativeId = useAppSelector((state) => (state.initiative.loaded ? state.initiative.data._id : ''));
  const latestHistorical = useAppSelector(getLatestReportData);

  const utrv1 = getDataAsValueData(latestHistorical?.reportData, PoliciesAndBestPracticeCode.Diversity);
  const utrv2 = getDataAsNumber(latestHistorical?.reportData, PoliciesAndBestPracticeCode.MentalHeal);
  const utrv3 = getDataAsValueData(latestHistorical?.reportData, PoliciesAndBestPracticeCode.BriberyCorruption);
  const utrv4 = getDataAsValueData(latestHistorical?.reportData, PoliciesAndBestPracticeCode.SustainabilityReporting);
  const utrv5 = getDataAsNumber(latestHistorical?.reportData, PoliciesAndBestPracticeCode.BonusesLinked);
  const utrv6 = getDataAsValueData(latestHistorical?.reportData, PoliciesAndBestPracticeCode.humanTrafficking);

  const diversityPolicy = utrv1 === 'yes';
  const mentalHealthPolicy = utrv2 === 100;
  const briberyCorruptionPolicy = utrv3 === 'yes';
  const sustainabilityReporting = utrv4 === 'yes';
  const bonusesLinkedToSustainability = utrv5 > 0;
  const humanTrafficingPolicy = utrv6 === 'yes';

  const TableHeader = () => (
    <th className='d-flex justify-content-between'>
      <span>Policy/Best practice</span>
      <span>Status</span>
    </th>
  );

  const policiesAndBestPractices: PolicyItem[][] = [
    [
      {
        utrCode: PoliciesAndBestPracticeCode.Diversity,
        name: 'Diversity policy',
        hasPositiveValue: diversityPolicy,
      },
      {
        utrCode: PoliciesAndBestPracticeCode.SustainabilityReporting,
        name: 'Sustainability reporting',
        hasPositiveValue: sustainabilityReporting,
      },
    ],
    [
      {
        utrCode: PoliciesAndBestPracticeCode.MentalHeal,
        name: 'Mental Health policy',
        hasPositiveValue: mentalHealthPolicy,
      },
      {
        utrCode: PoliciesAndBestPracticeCode.BonusesLinked,
        name: 'Bonuses linked to Sustainability',
        hasPositiveValue: bonusesLinkedToSustainability,
      },
    ],
    [
      {
        utrCode: PoliciesAndBestPracticeCode.BriberyCorruption,
        name: 'Bribery/Corruption policy',
        hasPositiveValue: briberyCorruptionPolicy,
      },
      {
        utrCode: PoliciesAndBestPracticeCode.humanTrafficking,
        name: 'Human Trafficking Supplier policy',
        hasPositiveValue: humanTrafficingPolicy,
      },
    ],
  ];

  function getName(policy: PolicyItem) {
    return <div className={'policy-text px-2 py-3'}>
      <span className={'mr-2'}>
      {policy.name}
      </span>
      <span>
      {policy.hasPositiveValue ? greenIcon : redIcon}
      </span>
    </div>;
  }

  return (
    <table className='policies-table w-100 mb-4'>
      <thead>
        <tr>
          <TableHeader />
          <th className='spacer'></th>
          <TableHeader />
        </tr>
      </thead>
      <tbody>
        {policiesAndBestPractices.map((row, index) => (
          <tr key={index}>
            {row.map((policy, index) => (
              <React.Fragment key={index}>
                <td>
                  <ReferenceLinks
                    utrCodes={[policy.utrCode]}
                    initiativeId={initiativeId}
                    getName={() => getName(policy)}
                    tooltipText={''}
                    className={'w-100 text-decoration-none'}
                  />
                </td>
                {index % 2 === 0 ? <th className='spacer'></th> : null}
              </React.Fragment>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
})
